import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Web as WebIcon,
  PhoneAndroid as MobileIcon,
  Api as ApiIcon,
  ShoppingCart as EcommerceIcon,
  Article as CmsIcon,
  Dashboard as DashboardIcon,
  Computer as DesktopIcon,
  Build as CustomIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface Template {
  id: string;
  name: string;
  description: string;
  templateType: string;
  isPublic: boolean;
  isDefault: boolean;
  usageCount: number;
  averageAccuracy?: number;
  createdAt: string;
  createdBy?: {
    firstName: string;
    lastName: string;
  };
}

const templateTypeIcons: Record<string, React.ReactNode> = {
  web_application: <WebIcon />,
  mobile_app: <MobileIcon />,
  api_development: <ApiIcon />,
  ecommerce: <EcommerceIcon />,
  cms: <CmsIcon />,
  dashboard: <DashboardIcon />,
  desktop_application: <DesktopIcon />,
  custom: <CustomIcon />
};

const templateTypeColors: Record<string, string> = {
  web_application: 'primary',
  mobile_app: 'secondary',
  api_development: 'info',
  ecommerce: 'success',
  cms: 'warning',
  dashboard: 'error',
  desktop_application: 'default',
  custom: 'default'
};

const EstimationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getEstimationTemplates();
      console.log('Templates response:', response);
      
      // Handle different response formats
      let templatesArray = [];
      if (Array.isArray(response)) {
        templatesArray = response;
      } else if (response?.data && Array.isArray(response.data)) {
        templatesArray = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        templatesArray = response.data.data;
      }
      
      console.log('Setting templates:', templatesArray.length);
      setTemplates(templatesArray);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await apiService.deleteEstimationTemplate(templateId);
        await loadTemplates();
      } catch (err) {
        setError('Failed to delete template');
      }
    }
  };

  const handleInitializeTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the API service - it now returns the full response
      const data = await apiService.initializeEstimationTemplates();
      
      // Reload templates
      await loadTemplates();
      
      // Show message based on response
      if (data.success) {
        if (data.created && data.created > 0) {
          alert(`✅ Successfully initialized ${data.created} default templates!`);
        } else if (data.alreadyExists && data.alreadyExists > 0) {
          alert(`ℹ️ ${data.alreadyExists} templates already exist. No new templates were created.\n\n${data.message || ''}`);
        } else {
          alert(data.message || 'Templates initialization completed.');
        }
      } else {
        alert(`❌ ${data.message || 'Failed to initialize templates'}`);
      }
      
      // Also log for debugging
      console.log('Initialize templates response:', data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to initialize templates';
      setError(errorMessage);
      alert(`❌ Error: ${errorMessage}`);
      console.error('Failed to initialize templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesType = filterType === 'all' || template.templateType === filterType;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const getAccuracyColor = (accuracy?: number) => {
    if (!accuracy) return 'default';
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'warning';
    return 'error';
  };

  const getAccuracyIcon = (accuracy?: number) => {
    if (!accuracy) return null;
    if (accuracy >= 80) return <CheckCircleIcon />;
    if (accuracy >= 60) return <WarningIcon />;
    return <WarningIcon />;
  };

  const renderTemplateCard = (template: Template) => (
    <Card key={template.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {templateTypeIcons[template.templateType]}
          <Typography variant="h6" component="div" flex={1}>
            {template.name}
          </Typography>
          <Box display="flex" gap={0.5}>
            {template.isDefault && (
              <Chip label="Default" size="small" color="primary" />
            )}
            {template.isPublic && (
              <Chip label="Public" size="small" color="secondary" />
            )}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {template.description}
        </Typography>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip
            label={template.templateType.replace('_', ' ')}
            size="small"
            color={templateTypeColors[template.templateType] as any}
            variant="outlined"
          />
          {template.averageAccuracy && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {getAccuracyIcon(template.averageAccuracy)}
              <Typography variant="body2" color={getAccuracyColor(template.averageAccuracy)}>
                {template.averageAccuracy.toFixed(1)}% accuracy
              </Typography>
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <TrendingUpIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {template.usageCount} uses
          </Typography>
          {template.createdBy && (
            <>
              <Typography variant="body2" color="text.secondary">•</Typography>
              <Typography variant="body2" color="text.secondary">
                by {template.createdBy.firstName} {template.createdBy.lastName}
              </Typography>
            </>
          )}
        </Box>

        <Box display="flex" gap={1}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewTemplate(template)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Template">
            <IconButton size="small" onClick={() => handleEditTemplate(template)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Template">
            <IconButton size="small">
              <CopyIcon />
            </IconButton>
          </Tooltip>
          {!template.isDefault && (
            <Tooltip title="Delete Template">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => handleDeleteTemplate(template.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderTemplateDialog = () => (
    <Dialog 
      open={dialogOpen} 
      onClose={() => setDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {dialogMode === 'view' && 'Template Details'}
        {dialogMode === 'edit' && 'Edit Template'}
        {dialogMode === 'create' && 'Create New Template'}
      </DialogTitle>
      <DialogContent>
        {selectedTemplate && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {selectedTemplate.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {selectedTemplate.description}
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Template Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Type</Typography>
                    <Typography variant="body2">
                      {selectedTemplate.templateType.replace('_', ' ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Usage Count</Typography>
                    <Typography variant="body2">
                      {selectedTemplate.usageCount} times
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Average Accuracy</Typography>
                    <Typography variant="body2">
                      {selectedTemplate.averageAccuracy?.toFixed(1) || 'N/A'}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Created</Typography>
                    <Typography variant="body2">
                      {new Date(selectedTemplate.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Complexity Matrix</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Frontend, Backend, Middleware, and Database complexity factors are configured 
                  for this template type.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Effort Multipliers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Project setup, management, testing, DevOps, documentation, and design multipliers 
                  are pre-configured for optimal estimation.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Common Patterns</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  This template includes common development patterns and their estimated complexity 
                  for this project type.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>
          Close
        </Button>
        {dialogMode === 'view' && (
          <Button variant="contained" onClick={() => handleEditTemplate(selectedTemplate!)}>
            Edit Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Estimation Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Pre-configured estimation templates for different project types
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={handleInitializeTemplates}
            disabled={loading}
          >
            Initialize Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search templates"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or description..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={filterType}
                  label="Template Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="web_application">Web Application</MenuItem>
                  <MenuItem value="mobile_app">Mobile App</MenuItem>
                  <MenuItem value="api_development">API Development</MenuItem>
                  <MenuItem value="ecommerce">E-commerce</MenuItem>
                  <MenuItem value="cms">CMS</MenuItem>
                  <MenuItem value="dashboard">Dashboard</MenuItem>
                  <MenuItem value="desktop_application">Desktop Application</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1}>
                <Chip label={`${filteredTemplates.length} templates`} color="primary" />
                <Chip label={`${templates.filter(t => t.isDefault).length} default`} color="secondary" />
                <Chip label={`${templates.filter(t => t.isPublic).length} public`} color="info" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : filteredTemplates.length > 0 ? (
        <Grid container spacing={3}>
          {filteredTemplates.map(renderTemplateCard)}
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by initializing default templates or creating a custom one.'
              }
            </Typography>
            <Box display="flex" gap={1} justifyContent="center">
              <Button variant="outlined" onClick={handleInitializeTemplates}>
                Initialize Defaults
              </Button>
              <Button variant="contained" onClick={handleCreateTemplate}>
                Create Template
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {renderTemplateDialog()}
    </Container>
  );
};

export default EstimationTemplates;
